import { Helmet } from "react-helmet-async";
import { SITE_URL, GSC_VERIFICATION, BUSINESS, img } from "@/config/site";
import { PRODUCTS } from "@/data/products";

export const absoluteUrl = (path = "/") => `${SITE_URL}${path}`;

export const Seo = ({ title, description, path = "/", image, noindex = false, jsonLd = [], type = "website" }) => {
  const url = absoluteUrl(path);
  const ogImage = image || img("ocean", 1200);
  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-ZA" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"} />
      <meta property="og:site_name" content="Uhlumo Organics" />
      <meta property="og:locale" content="en_ZA" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {GSC_VERIFICATION ? <meta name="google-site-verification" content={GSC_VERIFICATION} /> : null}
      {jsonLd.filter(Boolean).map((j, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(j)}
        </script>
      ))}
    </Helmet>
  );
};

const ORG_ID = () => `${SITE_URL}/#organization`;

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID(),
  name: BUSINESS.name,
  url: `${SITE_URL}/`,
  email: BUSINESS.email,
  telephone: BUSINESS.phoneE164,
  foundingDate: BUSINESS.established,
  founder: { "@type": "Person", name: BUSINESS.founder },
  address: postalAddress(),
  description: "Uhlumo Organics is a South African business that sources, packages, supplies and distributes natural wellness products from Gugulethu, Cape Town.",
});

const postalAddress = () => ({
  "@type": "PostalAddress",
  streetAddress: BUSINESS.address.line1,
  addressLocality: `${BUSINESS.address.suburb}, ${BUSINESS.address.city}`,
  addressRegion: BUSINESS.address.province,
  postalCode: BUSINESS.address.postalCode,
  addressCountry: BUSINESS.address.country,
});

export const localBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE_URL}/#localbusiness`,
  name: BUSINESS.name,
  url: `${SITE_URL}/`,
  telephone: BUSINESS.phoneE164,
  email: BUSINESS.email,
  image: img("ocean", 1200),
  address: postalAddress(),
  areaServed: { "@type": "City", name: "Cape Town" },
  parentOrganization: { "@id": ORG_ID() },
  makesOffer: PRODUCTS.map((p) => ({ "@type": "Offer", itemOffered: { "@type": "Product", name: p.fullName || p.name, url: `${SITE_URL}${p.path}` } })),
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${SITE_URL}/#website`,
  name: BUSINESS.name,
  url: `${SITE_URL}/`,
  publisher: { "@id": ORG_ID() },
  inLanguage: "en-ZA",
});

export const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: absoluteUrl(it.path) })),
});

// Product schema intentionally omits price, availability, ratings and reviews
// until verified information exists.
export const productSchema = (p) => ({
  "@context": "https://schema.org",
  "@type": "Product",
  name: p.fullName || p.name,
  description: p.shortDescription,
  category: p.category,
  url: absoluteUrl(p.path),
  brand: { "@type": "Brand", name: BUSINESS.name },
  manufacturer: { "@id": ORG_ID() },
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

export const itemListSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Uhlumo Organics natural wellness products",
  itemListElement: PRODUCTS.map((p, i) => ({ "@type": "ListItem", position: i + 1, url: absoluteUrl(p.path), name: p.fullName || p.name })),
});
